/**
 * Onde o painel de detalhe aparece quando a grade o tira do lugar.
 *
 * Mora no `screen-engine` — e não junto da grade — por duas razões:
 *
 *  - **desenhar nó é do motor.** Aqui é `<tem-node-host>`, o mesmo despachante
 *    que desenha a tela inteira: o painel dentro da gaveta é o MESMO componente
 *    que estaria na tela, com o mesmo formulário. Nada de mover DOM, que é o que
 *    o canvas antigo fazia (e que produzia o campo preenchido "por fora", com
 *    `<input>` procurado no documento);
 *  - **sobrevive à Fase 8.** A grade ainda é o componente legado, e quem liga os
 *    dois é um renderer que será apagado. Esta peça fica.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';

import { ScreenNode } from '../../model/node-props';
import { trackByNodeId } from '../../model/screen-index';
import { NodeHostComponent } from '../node-host.component';
import { RenderMode } from '../node-renderer';

/** Largura da gaveta quando ninguém escolheu, em % da tela. */
const DEFAULT_DRAWER_WIDTH = 40;

@Component({
  selector: 'tem-detail-surface',
  standalone: true,
  imports: [CommonModule, NzDrawerModule, NodeHostComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (surface() === 'drawer') {
      <nz-drawer
        [nzVisible]="open()"
        [nzWidth]="drawerWidth()"
        [nzTitle]="title()"
        nzPlacement="right"
        (nzOnClose)="closed.emit()">

        <ng-container *nzDrawerContent>
          <ng-container [ngTemplateOutlet]="conteudo" />
        </ng-container>
      </nz-drawer>

    } @else if (open()) {
      <!-- Virar a tela: a grade esconde a face dela sozinha, entao aqui basta
           desenhar o painel no lugar que sobrou. -->
      <div class="tem-detail-flip">
        <ng-container [ngTemplateOutlet]="conteudo" />
      </div>
    }

    <ng-template #conteudo>
      @for (panel of panels(); track trackBy(0, panel)) {
        <tem-node-host [node]="panel" [form]="form()" [mode]="mode()" />
      } @empty {
        <p class="tem-detail-empty">{{ emptyText() }}</p>
      }
    </ng-template>
  `,
  styles: [`
    :host { display: contents; }

    .tem-detail-flip {
      border: 1px solid var(--tem-border);
      border-radius: var(--tem-radius);
      background: var(--tem-surface);
      padding: var(--tem-space-3);
    }

    .tem-detail-empty {
      margin: 0;
      color: var(--tem-muted);
      font-size: var(--tem-fs-sm);
    }
  `]
})
export class DetailSurfaceComponent {

  readonly panels = input.required<readonly ScreenNode[]>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  /** `drawer` desenha gaveta; `flip` desenha no lugar da grade. */
  readonly surface = input<'drawer' | 'flip'>('drawer');
  readonly open = input<boolean>(false);
  readonly title = input<string>('');

  /**
   * Texto de quando não há painel nenhum.
   *
   * Vem de fora traduzido: este componente é do motor, e o motor não decide
   * texto. Sem ele a gaveta abriria em branco, que é o "vira mas sem campos".
   */
  readonly emptyText = input<string>('');

  readonly trackBy = trackByNodeId;

  readonly drawerWidth = computed<string>(() => {
    const escolhida = this.rawWidth();

    return typeof escolhida === 'number' && escolhida >= 10 && escolhida <= 90
      ? escolhida + '%'
      : DEFAULT_DRAWER_WIDTH + '%';
  });

  /** Largura pedida, em % da tela. */
  readonly rawWidth = input<number | undefined>(undefined);

  readonly closed = output<void>();
}
